import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";
import { Grid, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { DashboardCard } from "../../../../components/cards/dashboardCard";
import { TableComponent } from "../../../../components/table/table";
import { PageTitle } from "../../../../components/typography/pageTitle";
import { formatToCurrency, getTimeAgo } from "../../../../helperFunctions";
import { useGetUserDashboardSummaryQuery } from "../../../../redux/features/auth/auth.api";
import { useAppSelector } from "../../../../redux/hooks";
import { DashboardTransactionTypeMap } from "../../../../utils/constants";

export const MemberDashboard = () => {
  const { profile } = useAppSelector((state) => state.auth);

  const navigate = useNavigate();

  const { data: dashSumary, isLoading: loadingDash } =
    useGetUserDashboardSummaryQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  return (
    <>
      <div className="hidden md:flex">
        <PageTitle
          text={`Welcome back, ${profile?.FirstName}`}
          subtext="Track your growth, manage your contributions, and stay connected with your cooperative."
          className=""
        />
      </div>

      <Swiper
        modules={[Grid, Pagination]}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 16,
            grid: { rows: 2, fill: "row" },
          },
          1024: {
            slidesPerView: 2,
            spaceBetween: 16,
            grid: { rows: 2, fill: "row" }, // Show 2 rows on desktop
          },
        }}
        className="pb-10 md:pb-0 md:mt-6 w-full !max-w-full"
        style={{
          width: "100%",
        }}
      >
        <SwiperSlide>
          <DashboardCard
            title="Contributions"
            value={dashSumary?.data?.saving?.total ?? 0}
            changeValue={dashSumary?.data?.saving?.trend?.change_percent ?? 0}
            changeType={
              (dashSumary?.data?.saving?.trend?.change_percent ?? 0) < 0
                ? "decrement"
                : "increment"
            }
            loading={loadingDash}
            className="bg-[#E2F6DF]"
            icon="coin-swap"
            iconClassName="text-white bg-[#7AAD72]"
          />
        </SwiperSlide>

        <SwiperSlide>
          <DashboardCard
            title="Loan Balance"
            value={dashSumary?.data?.loan?.total ?? 0}
            changeValue={dashSumary?.data?.loan?.trend?.change_percent ?? 0}
            changeType={
              (dashSumary?.data?.loan?.trend?.change_percent ?? 0) < 0
                ? "decrement"
                : "increment"
            }
            loading={loadingDash}
            className="bg-[#D5F1F7]"
            icon="coins-hand"
            iconClassName="text-white bg-[#49A9BE]"
          />
        </SwiperSlide>

        <SwiperSlide>
          <DashboardCard
            title="Deposits"
            value={dashSumary?.data?.deposit?.total ?? 0}
            changeValue={dashSumary?.data?.deposit?.trend?.change_percent ?? 0}
            changeType={
              (dashSumary?.data?.deposit?.trend?.change_percent ?? 0) < 0
                ? "decrement"
                : "increment"
            }
            loading={loadingDash}
            className="bg-[#ECEDE6]"
            icon="credit-card-down"
            iconClassName="text-white bg-[#999999]"
          />
        </SwiperSlide>

        <SwiperSlide>
          <DashboardCard
            title="Shares"
            value={dashSumary?.data?.share?.total ?? 0}
            changeValue={dashSumary?.data?.share?.trend?.change_percent ?? 0}
            changeType={
              (dashSumary?.data?.share?.trend?.change_percent ?? 0) < 0
                ? "decrement"
                : "increment"
            }
            loading={loadingDash}
            className="bg-[#F9EFFD]"
            icon="pie-chart"
            iconClassName="text-white bg-[#9F6EB3]"
          />
        </SwiperSlide>
      </Swiper>

      <div className="flex flex-col gap-3 md:gap-6 mt-8">
        <div>
          <p className="text-base text-pry-dark-green-400 font-medium">
            Recent activities
          </p>
        </div>

        <TableComponent
          headers={[]}
          isRowClickable
          rowClassName="!py-3"
          rows={
            dashSumary?.data?.logs?.results?.map((log, idx) => {
              return {
                id: idx,
                onClick: () => {
                  navigate(
                    `/app/my-cooperatives/${encodeURIComponent(
                      log.Cooperative__CoopName
                    )}/${log.Cooperative_id}?tab=${
                      DashboardTransactionTypeMap[log.source].cooperativeTab
                    }`
                  );
                },
                items: [
                  <div className="flex flex-row gap-4 items-center">
                    <div className="flex flex-col gap-1 flex-1">
                      <p className="text-sm font-medium max-w-[80%] text-ellipsis overflow-hidden">
                        {log.Cooperative__CoopName}
                      </p>
                      <p className="text-pry-dark-green-300/50 text-xs">
                        {DashboardTransactionTypeMap[log.source].text} {" ∙ "}
                        <span className="">
                          {getTimeAgo(log.unified_date ?? "")}
                        </span>
                      </p>
                    </div>

                    <div className="flex flex-col gap-1 items-end">
                      <p
                        className={classNames(
                          "font-bold py-0.5 px-2 rounded-full",
                          {
                            "text-pry-green-300 bg-light-green-bg-500":
                              log.flow_type === "in",
                            "text-red-200 bg-light-red-bg-50":
                              log.flow_type === "out",
                          }
                        )}
                      >
                        {log.flow_type === "in" ? "+" : "-"}{" "}
                        {formatToCurrency(log.Amount, { currency: "NGN" })}
                      </p>
                    </div>
                  </div>,
                ],
              };
            }) ?? []
          }
          isPaginated={false}
          isLoading={loadingDash}
          loadingHeight={32}
          isTableEmpty={[0, undefined].includes(
            dashSumary?.data?.logs?.results?.length
          )}
          emptyTitle="No recent activities"
        />
      </div>
    </>
  );
};
